import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/firebase';
import './ApiKeyManagement.css';

const ApiKeyManagement = () => {
  const { user: adminUser, loading: authLoading } = useContext(AuthContext);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Create new API key form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  
  // API logs state
  const [showLogs, setShowLogs] = useState(false);
  const [apiLogs, setApiLogs] = useState([]);

  const manageApiKeys = httpsCallable(functions, 'manageApiKeys');

  useEffect(() => {
    // Only fetch API keys if admin user is loaded
    if (adminUser) {
      fetchApiKeys();
    }
  }, [adminUser]);

  const fetchApiKeys = async () => {
    setLoading(true);
    setError(null);
    
    console.log('🔑 Fetching API keys...', { adminUser: adminUser?.email, role: adminUser?.role });
    
    try {
      const result = await manageApiKeys({ action: 'list' });
      
      if (result.data.success) {
        setApiKeys(result.data.data || []);
      } else {
        setError(result.data.message || 'Failed to fetch API keys');
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
      setError('Error fetching API keys: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (e) => {
    e.preventDefault();
    
    if (!newClientName.trim()) {
      setError('Client name is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await manageApiKeys({
        action: 'create',
        clientName: newClientName.trim()
      });
      
      if (result.data.success) {
        setCreatedKey(result.data.data);
        setSuccess('API key created successfully');
        setNewClientName('');
        setShowCreateForm(false);
        fetchApiKeys(); // Refresh the list
      } else {
        setError(result.data.message || 'Failed to create API key');
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      setError('Error creating API key: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await manageApiKeys({
        action: 'revoke',
        keyId: keyId
      });
      
      if (result.data.success) {
        setSuccess('API key revoked successfully');
        fetchApiKeys(); // Refresh the list
      } else {
        setError(result.data.message || 'Failed to revoke API key');
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
      setError('Error revoking API key: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess(`${label} copied to clipboard`);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge bg-success',
      revoked: 'badge bg-danger',
      suspended: 'badge bg-warning'
    };
    return badges[status] || 'badge bg-secondary';
  };

  // Show loading if auth is still loading
  if (authLoading) {
    return (
      <div className="api-key-management text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading authentication...</span>
        </div>
        <p className="mt-2 text-muted">Verifying admin access...</p>
      </div>
    );
  }

  // Show error if user is not authenticated
  if (!adminUser) {
    return (
      <div className="api-key-management">
        <div className="alert alert-danger">
          <h5>Authentication Required</h5>
          <p>You must be logged in as an admin to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="api-key-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>External API Key Management</h3>
        <button 
          className="btn btn-primary"
          onClick={() => setShowCreateForm(true)}
          disabled={loading}
        >
          <i className="fas fa-plus me-2"></i>
          Create New API Key
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible" role="alert">
          {success}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setSuccess(null)}
          ></button>
        </div>
      )}

      {/* Created API Key Display */}
      {createdKey && (
        <div className="alert alert-info">
          <h5>🎉 New API Key Created</h5>
          <p><strong>Important:</strong> Save these credentials securely. The secret will not be shown again.</p>
          
          <div className="mb-2">
            <label className="form-label fw-bold">API Key:</label>
            <div className="input-group">
              <input 
                type="text" 
                className="form-control" 
                value={createdKey.apiKey} 
                readOnly 
              />
              <button 
                className="btn btn-outline-secondary" 
                onClick={() => copyToClipboard(createdKey.apiKey, 'API Key')}
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>
          
          <div className="mb-2">
            <label className="form-label fw-bold">Secret:</label>
            <div className="input-group">
              <input 
                type="text" 
                className="form-control" 
                value={createdKey.secret} 
                readOnly 
              />
              <button 
                className="btn btn-outline-secondary" 
                onClick={() => copyToClipboard(createdKey.secret, 'Secret')}
              >
                <i className="fas fa-copy"></i>
              </button>
            </div>
          </div>
          
          <button 
            className="btn btn-secondary btn-sm mt-2" 
            onClick={() => setCreatedKey(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Create New API Key Form */}
      {showCreateForm && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Create New API Key</h5>
          </div>
          <div className="card-body">
            <form onSubmit={handleCreateApiKey}>
              <div className="mb-3">
                <label htmlFor="clientName" className="form-label">
                  Client Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="clientName"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="e.g., Partner Company Inc."
                  required
                />
                <div className="form-text">
                  Identify the third party that will use this API key
                </div>
              </div>
              
              <div className="d-flex gap-2">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating...
                    </>
                  ) : (
                    'Create API Key'
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewClientName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Active API Keys ({apiKeys.length})</h5>
          <button 
            className="btn btn-sm btn-outline-primary"
            onClick={fetchApiKeys}
            disabled={loading}
          >
            <i className="fas fa-refresh me-1"></i>
            Refresh
          </button>
        </div>
        <div className="card-body p-0">
          {loading && apiKeys.length === 0 ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-key fa-3x text-muted mb-3"></i>
              <p className="text-muted">No API keys created yet</p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateForm(true)}
              >
                Create First API Key
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Client Name</th>
                    <th>API Key</th>
                    <th>Status</th>
                    <th>Rate Limits</th>
                    <th>Created</th>
                    <th>Last Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr key={key.id}>
                      <td>
                        <div className="fw-bold">{key.clientName}</div>
                        <small className="text-muted">
                          Requests: {key.requestCount || 0}
                        </small>
                      </td>
                      <td>
                        <code className="text-muted">
                          {key.keyId ? `${key.keyId.substring(0, 12)}...` : 'N/A'}
                        </code>
                      </td>
                      <td>
                        <span className={getStatusBadge(key.status)}>
                          {key.status}
                        </span>
                      </td>
                      <td>
                        <small>
                          {key.rateLimit?.requestsPerMinute || 10}/min<br/>
                          {key.rateLimit?.requestsPerDay || 1000}/day
                        </small>
                      </td>
                      <td>
                        <small>{formatDate(key.createdAt)}</small>
                      </td>
                      <td>
                        <small>{formatDate(key.lastUsed)}</small>
                      </td>
                      <td>
                        {key.status === 'active' && (
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRevokeApiKey(key.id)}
                            disabled={loading}
                          >
                            <i className="fas fa-ban me-1"></i>
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* API Usage Instructions */}
      <div className="card mt-4">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="fas fa-book me-2"></i>
            API Usage Instructions
          </h5>
        </div>
        <div className="card-body">
          <h6>Endpoint:</h6>
          <code>POST https://your-project.cloudfunctions.net/checkUserStatus</code>
          
          <h6 className="mt-3">Headers:</h6>
          <pre className="bg-light p-2 rounded">
{`X-API-Key: your_api_key
X-Signature: hmac_sha256_signature
Content-Type: application/json`}
          </pre>
          
          <h6 className="mt-3">Request Body:</h6>
          <pre className="bg-light p-2 rounded">
{`{
  "email": "user@example.com"
}`}
          </pre>
          
          <h6 className="mt-3">Response:</h6>
          <pre className="bg-light p-2 rounded">
{`{
  "success": true,
  "data": {
    "email": "user@example.com",
    "status": "approved",
    "customerId": "RS20250205001",
    "lastUpdated": "2025-01-27T10:30:00Z"
  },
  "message": "User status retrieved successfully"
}`}
          </pre>
          
          <div className="alert alert-info mt-3">
            <strong>Note:</strong> The X-Signature header must contain an HMAC-SHA256 signature 
            of the request body using your API secret.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyManagement; 